// In-memory identifier index per connection (Phase 2 — NAV2 §2.3).
// PRD: Ralph/NavigationalFeatures/PRD.md Slice 16.
//
// Two hashmaps:
//   - byIdentifier — `${identifierKind}::${db}::${schema}::${name}` (lower-cased
//     folded forms) → ReferenceRecord[]. Powers `query(target, kind)`. The
//     COLUMN kind leaves `db` and `schema` empty on both sides — columns are
//     recorded bare; see `identifierKey`.
//   - byObject — `${objectKind}::${db}::${schema}::${name}` → ReferenceRecord[].
//     Powers `invalidate(objectKind, objectName)` in O(refs-in-object) without
//     scanning the byIdentifier map.
//   - byLeaf — `${'relation'|'routine'}::${lowercased leaf}` → ReferenceRecord[].
//     The broad candidate lookup `queryRelationCandidates(target, kind)` that
//     the schema-aware comparators narrow; see `leafBucketFor`.
//
// Storage is per-connection; lifecycle is owned by Slice 23 (connection
// connect/disconnect wiring). The API is synchronous — JavaScript's
// single-threaded execution model is the thread-safety guarantee. Any async
// fetch/parse happens upstream in Slices 17 (objectBodyFetcher) + 18–21
// (per-kind index builders). Those callers deliberately do NOT serialize
// their fetches: a DDL refresh frees its coalescing slot before fetching, so
// two refreshes of one object — and a rename overlapping either — run their
// reads concurrently. What orders them is the SWAP, not the fetch: each write
// claims a ticket keyed by the identity this index files the records under
// (`objectKeyFor`), and a write whose ticket is no longer current is refused.
// See `indexInvalidator.ts`'s `takeRefreshTicket`.

import { relationLeafKey } from '../relationIdentity';
import type {
  IdentifierIndex,
  IdentifierIndexStats,
  IdentifierKind,
  ObjectKeyCase,
  ObjectKind,
  QualifiedName,
  ReferenceRecord,
} from '../types';

// Declared with the `IdentifierIndex` contract in `types.ts` (the index itself
// reports the policy it keyed with), re-exported here because this module owns
// everything that COMPUTES an object key.
export type { ObjectKeyCase } from '../types';

// This is a COARSE, case-insensitive bucket key, used by the exact-match
// `query` (columns/routines), object bucketing, and `invalidate`. Per CK-B
// MUST-FIX 5 the RELATION schema-aware identity decision is NOT built on this
// key — "candidate lookup may be deliberately broad, but final matching must
// use the comparator". Relation AND routine candidate retrieval go through the
// broad leaf-name `byLeaf` map + `queryRelationCandidates`, and the
// engine-aware, case-correct, quoted-distinction-preserving final match runs
// in `matchRelationRef` / `relationIdentitiesEqual` (relationIdentity.ts) and
// `matchRoutineRef` (routineIdentity.ts).
// Keeping this coarse key case-insensitive preserves cross-case robustness for
// the exact-match + invalidation paths (catalog vs walker fold drift, mssql
// quoted-vs-unquoted) without weakening #34's relation matching.
// The COLUMN / ROUTINE readers narrow this key's answer the same way the
// relation readers narrow theirs: `relationIdentity.ts`'s
// `catalogLeafRefMatches` drops a case-sensitive SQL Server database's `dbo.p`
// from a search for `dbo.P` (and `T.id` from a rename of `T.Id`), and
// PostgreSQL's / Oracle's quoted `"Id"` from a search for `id` — on those two
// engines the FOLDED form is the identity, so lowercasing it here is coarser
// than the catalog. Both readers
// (`find-references/findReferencesService.ts`, `rename/renamePlanner.ts`)
// apply it. The key itself stays coarse on purpose — it is the discovery half.
//
// For a ROUTINE the key also keeps the qualification the BODY spelled, which
// is why the exact key alone cannot answer a routine search: `SELECT fn(x)`
// keys `routine::..fn` while `SELECT dbo.fn(x)` keys `routine::.dbo.fn`, and
// the reader arrives with the TARGET's qualification. See `routineIdentity.ts`
// for the full account. Columns are the ONLY kind keyed bare.
function foldedKey(name: QualifiedName, bareLeafOnly: boolean): string {
  const db = bareLeafOnly ? '' : (name.foldedDatabase ?? '').toLowerCase();
  const schema = bareLeafOnly ? '' : (name.foldedSchema ?? '').toLowerCase();
  const ident = (name.foldedName ?? name.name ?? '').toLowerCase();
  return `${db}::${schema}::${ident}`;
}

const RELATION_KINDS: ReadonlySet<IdentifierKind> = new Set<IdentifierKind>(['table', 'view']);

/** Which broad leaf-name bucket a record's kind belongs to, or `null` for the
 *  kinds the exact-match `query` answers on its own.
 *
 *  COLUMNS need no bucket: they are recorded bare on both sides, so their exact
 *  key already IS a leaf key. RELATIONS and ROUTINES both keep their
 *  qualification in the exact key — a schema is part of their identity — while
 *  emitter and reader spell that qualification differently, so both need a
 *  discovery bucket that the engine-aware comparator then narrows
 *  (`relationIdentity.ts`'s `matchRelationRef`, `routineIdentity.ts`'s
 *  `matchRoutineRef`). */
function leafBucketFor(kind: IdentifierKind): 'relation' | 'routine' | null {
  if (RELATION_KINDS.has(kind)) return 'relation';
  return kind === 'routine' ? 'routine' : null;
}

function leafCandidateKey(bucket: 'relation' | 'routine', name: QualifiedName): string {
  return `${bucket}::${relationLeafKey(name)}`;
}

/** Every COLUMN emitter records the identifier BARE — `index/viewWalker.ts`'s
 *  `canonicalizeColumn`, `index/columnRefScanner.ts`'s and
 *  `index/constraintExpressionScanner.ts`'s `canonicalize` all keep only
 *  `{name, raw, quoted, foldedName}` — because a column ref's qualifier in a
 *  body is an ALIAS (`u.email`), buffer-scoped noise that would split the
 *  index across every alias spelling.
 *
 *  A READER, though, arrives qualified: `go-to/columnAtCursor.ts` returns the
 *  column token plus the host relation's schema and database, so F2 and
 *  Shift+F12 on `B.dbo.users.email` — and on a plain `users.email`, which the
 *  OE lookup qualifies just the same — asked for `column::b::dbo::email` while
 *  every record sat under `column::::email`. The lookup answered nothing:
 *  Find References said "no references", and Apply renamed the column without
 *  rewriting one dependent body.
 *
 *  So the column key drops its qualification on BOTH sides — symmetric, which
 *  is what keeps `invalidate` (also keyed through here) exact. Nothing is lost:
 *  no emitter ever records a qualified column. Cross-table disambiguation never
 *  lived in this key anyway — it lives in each record's `hostTable` versus the
 *  rename's `containingTable` (`rename/renameReferenceCollection.ts`'s
 *  `filterToContainingTable`), and the per-engine case decision lives in
 *  `relationIdentity.ts`'s `catalogLeafRefMatches`. */
function identifierKey(name: QualifiedName, kind: IdentifierKind): string {
  return `${kind}::${foldedKey(name, kind === 'column')}`;
}

/** What every consumer uses when the engine's policy is not known yet —
 *  over-inclusive, and the only safe direction while the answer is missing. */
export const FOLD_BOTH: ObjectKeyCase = { schema: false, name: false };

/** The object key under `policy` — each component folded or kept on its own.
 *  Exported because find-references and rename GROUP by object and must draw
 *  the same line the index does: grouping that folds what the index keeps
 *  apart merges two objects' ranges into one group, against one body. */
export function objectKeyFor(kind: ObjectKind, name: QualifiedName, policy: ObjectKeyCase): string {
  const db = policy.schema ? (name.database ?? '') : (name.foldedDatabase ?? '').toLowerCase();
  const schema = policy.schema ? (name.schema ?? '') : (name.foldedSchema ?? '').toLowerCase();
  const leaf = policy.name ? name.name : (name.foldedName ?? name.name ?? '').toLowerCase();
  return `${kind}::${db}::${schema}::${leaf}`;
}

export type IdentifierIndexOptions = {
  /** How object keys treat case. `true` / `false` set both name components at
   *  once, which is all a throwaway buffer index or a unit fixture needs; a
   *  buffer index leaves it off entirely. Production passes a RESOLVER —
   *  `objectKeyCaseFor` in `connection-lifecycle/navCasePolicy.ts`, called with
   *  the object KIND and answering per component (see {@link ObjectKeyCase}).
   *  It has to be a resolver because MySQL's rule differs per kind AND per
   *  component, and because SQL Server's and MySQL's answers arrive with a
   *  probe: it is consulted per key until the first record of that kind, when
   *  the kind's policy is frozen so every key of this index is computed the
   *  same way. `undefined` from the resolver means "not known yet" → both
   *  components folded. Since 2026-09-12 the lifecycle learns the engine's
   *  setting BEFORE it creates the index, so `undefined` cannot reach a live
   *  build — only a unit test or a buffer index can still hit it. */
  readonly caseSensitiveObjects?: boolean | ((kind: ObjectKind) => ObjectKeyCase | undefined);
};

export function createIdentifierIndex(
  connectionId: string,
  options?: IdentifierIndexOptions,
): IdentifierIndex {
  // Reserved for future per-connection telemetry/logging — Slice 23 lifecycle
  // wiring will surface it. Keeps the factory signature stable now.
  void connectionId;
  const caseOption = options?.caseSensitiveObjects;
  const frozenByKind = new Map<ObjectKind, ObjectKeyCase>();
  const keyCaseFor = (kind: ObjectKind): ObjectKeyCase => {
    if (typeof caseOption !== 'function') {
      return caseOption === true ? { schema: true, name: true } : FOLD_BOTH;
    }
    return frozenByKind.get(kind) ?? caseOption(kind) ?? FOLD_BOTH;
  };
  const objectKey = (kind: ObjectKind, name: QualifiedName): string =>
    objectKeyFor(kind, name, keyCaseFor(kind));

  const byIdentifier = new Map<string, ReferenceRecord[]>();
  const byObject = new Map<string, ReferenceRecord[]>();
  // #34: broad candidate map keyed on `${bucket}::${lowercased leaf}`, so
  // schema-aware rename / find-references can over-collect every same-named
  // relation — or routine — across schemas/db/case and then narrow with
  // `matchRelationRef` / `matchRoutineRef`. The bucket keeps the two kinds'
  // leaves from colliding (a `users` table next to a `users()` function).
  const byLeaf = new Map<string, ReferenceRecord[]>();
  let parseFailed = 0;
  // Set by `buildIndexForConnection` when the body-byte budget or the row
  // ceiling stopped the fetch early — the index then describes only PART of
  // the connection's parseable bodies.
  let partial = false;

  function record(ref: ReferenceRecord): void {
    // Freeze the kind's key policy with its first record — see `IdentifierIndexOptions`.
    if (typeof caseOption === 'function' && !frozenByKind.has(ref.objectKind)) {
      frozenByKind.set(ref.objectKind, keyCaseFor(ref.objectKind));
    }
    const idKey = identifierKey(ref.identifier, ref.identifierKind);
    const objKey = objectKey(ref.objectKind, ref.objectName);

    const idBucket = byIdentifier.get(idKey);
    if (idBucket === undefined) byIdentifier.set(idKey, [ref]);
    else idBucket.push(ref);

    const objBucket = byObject.get(objKey);
    if (objBucket === undefined) byObject.set(objKey, [ref]);
    else objBucket.push(ref);

    const bucket = leafBucketFor(ref.identifierKind);
    if (bucket !== null) {
      const leaf = leafCandidateKey(bucket, ref.identifier);
      const leafBucket = byLeaf.get(leaf);
      if (leafBucket === undefined) byLeaf.set(leaf, [ref]);
      else leafBucket.push(ref);
    }

    if (ref.parseStatus === 'parse-failed') parseFailed += 1;
  }

  function query(
    target: QualifiedName,
    kind: IdentifierKind,
  ): readonly ReferenceRecord[] {
    return byIdentifier.get(identifierKey(target, kind)) ?? [];
  }

  function queryRelationCandidates(
    target: QualifiedName,
    kind: IdentifierKind = 'table',
  ): readonly ReferenceRecord[] {
    const bucket = leafBucketFor(kind);
    if (bucket === null) return [];
    return byLeaf.get(leafCandidateKey(bucket, target)) ?? [];
  }

  function invalidate(kind: ObjectKind, name: QualifiedName): void {
    const objKey = objectKey(kind, name);
    const removed = byObject.get(objKey);
    if (removed === undefined) return;

    byObject.delete(objKey);

    // Group removals by idKey so we splice each affected bucket once.
    const removedByIdKey = new Map<string, Set<ReferenceRecord>>();
    for (const ref of removed) {
      const idKey = identifierKey(ref.identifier, ref.identifierKind);
      let set = removedByIdKey.get(idKey);
      if (set === undefined) {
        set = new Set<ReferenceRecord>();
        removedByIdKey.set(idKey, set);
      }
      set.add(ref);
      if (ref.parseStatus === 'parse-failed') parseFailed -= 1;
    }

    for (const [idKey, refsToRemove] of removedByIdKey) {
      const bucket = byIdentifier.get(idKey);
      if (bucket === undefined) continue;
      const filtered = bucket.filter((r) => !refsToRemove.has(r));
      if (filtered.length === 0) byIdentifier.delete(idKey);
      else byIdentifier.set(idKey, filtered);
    }

    // Mirror the removal in the leaf-candidate map. `invalidate` is keyed by
    // OBJECT, never by identifier, so this stays correct however the emitters
    // spelled the identifiers inside that object's body.
    const removedSet = new Set(removed);
    const touchedLeaves = new Set<string>();
    for (const ref of removed) {
      const bucket = leafBucketFor(ref.identifierKind);
      if (bucket !== null) touchedLeaves.add(leafCandidateKey(bucket, ref.identifier));
    }
    for (const leaf of touchedLeaves) {
      const bucket = byLeaf.get(leaf);
      if (bucket === undefined) continue;
      const filtered = bucket.filter((r) => !removedSet.has(r));
      if (filtered.length === 0) byLeaf.delete(leaf);
      else byLeaf.set(leaf, filtered);
    }
  }

  function listObjectsOfKind(kind: ObjectKind): readonly QualifiedName[] {
    const prefix = `${kind}::`;
    const names: QualifiedName[] = [];
    for (const [key, bucket] of byObject) {
      if (key.startsWith(prefix) && bucket.length > 0) names.push(bucket[0].objectName);
    }
    return names;
  }

  function clear(): void {
    byIdentifier.clear();
    byObject.clear();
    byLeaf.clear();
    parseFailed = 0;
    partial = false;
    // An empty index may adopt a policy that has since become known.
    frozenByKind.clear();
  }

  function stats(): IdentifierIndexStats {
    let referenceCount = 0;
    for (const bucket of byObject.values()) referenceCount += bucket.length;
    return {
      objectCount: byObject.size,
      referenceCount,
      parseFailed,
    };
  }

  return {
    record,
    query,
    queryRelationCandidates,
    invalidate,
    listObjectsOfKind,
    clear,
    stats,
    markPartial: () => {
      partial = true;
    },
    isPartial: () => partial,
    objectKeyCaseFor: keyCaseFor,
  };
}
